import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getComplaintCategoriesMulti,
    createComplaintCategory,
    bulkCreateComplaintCategories,
    updateComplaintCategory,
    deleteComplaintCategory,
} from '../../../redux/actions/SocietyPortal/ComplaintAction/ComplaintAction';
import { getUsernamesDropdown } from '../../../redux/actions/SocietyPortal/EmployeeAction/EmployeeAction';
import { getSocietyDropdown } from '../../../redux/actions/SocietyController/CtrlEmployeeAction/CtrlEmployeeAction';
import { notify } from '../../../utils/notification';

// Reused palette + icon library to keep the visual language identical to the
// society-portal categories page — the badges rendered on the complaint list
// look the same regardless of who created the category.
const COLOR_PALETTE = [
    { key: 'rose',    bg: 'tw-bg-rose-100',    text: 'tw-text-rose-700',    ring: 'tw-ring-rose-500',    swatch: 'tw-bg-rose-500' },
    { key: 'amber',   bg: 'tw-bg-amber-100',   text: 'tw-text-amber-700',   ring: 'tw-ring-amber-500',   swatch: 'tw-bg-amber-500' },
    { key: 'emerald', bg: 'tw-bg-emerald-100', text: 'tw-text-emerald-700', ring: 'tw-ring-emerald-500', swatch: 'tw-bg-emerald-500' },
    { key: 'blue',    bg: 'tw-bg-blue-100',    text: 'tw-text-blue-700',    ring: 'tw-ring-blue-500',    swatch: 'tw-bg-blue-500' },
    { key: 'violet',  bg: 'tw-bg-violet-100',  text: 'tw-text-violet-700',  ring: 'tw-ring-violet-500',  swatch: 'tw-bg-violet-500' },
    { key: 'cyan',    bg: 'tw-bg-cyan-100',    text: 'tw-text-cyan-700',    ring: 'tw-ring-cyan-500',    swatch: 'tw-bg-cyan-500' },
    { key: 'slate',   bg: 'tw-bg-slate-200',   text: 'tw-text-slate-700',   ring: 'tw-ring-slate-500',   swatch: 'tw-bg-slate-500' },
    { key: 'fuchsia', bg: 'tw-bg-fuchsia-100', text: 'tw-text-fuchsia-700', ring: 'tw-ring-fuchsia-500', swatch: 'tw-bg-fuchsia-500' },
];

const ICON_LIBRARY = [
    { key: 'mdi:pipe-wrench',            label: 'Plumbing' },
    { key: 'mdi:lightning-bolt',         label: 'Electrical' },
    { key: 'mdi:shield-check-outline',   label: 'Security' },
    { key: 'mdi:broom',                  label: 'Housekeeping' },
    { key: 'mdi:tools',                  label: 'Maintenance' },
    { key: 'mdi:volume-vibrate',         label: 'Noise' },
    { key: 'mdi:car-outline',            label: 'Parking' },
    { key: 'mdi:elevator',               label: 'Elevator' },
    { key: 'mdi:water',                  label: 'Water' },
    { key: 'mdi:trash-can-outline',      label: 'Garbage' },
    { key: 'mdi:bug-outline',            label: 'Pest' },
    { key: 'mdi:home-city-outline',      label: 'Common Area' },
    { key: 'mdi:receipt-text-outline',   label: 'Billing' },
    { key: 'mdi:wifi',                   label: 'Internet' },
    { key: 'mdi:paw-outline',            label: 'Pets' },
    { key: 'mdi:message-alert-outline',  label: 'Other' },
];

const colorFor = (key) => COLOR_PALETTE.find(c => c.key === key) || COLOR_PALETTE[6];
const iconFor = (key) => key || 'mdi:tag-outline';

const emptyForm = { name: '', description: '', icon: 'mdi:message-alert-outline', color: 'slate', default_assignee_user_id: '' };

const fullName = (u) => {
    if (!u) return '';
    const full = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
    return full || u.email || `User #${u.user_id}`;
};

/**
 * Reusable multi-select popover for societies.
 * Renders a summary button; opens a search-filterable list with a Select-All
 * toggle. `selected` is an array of numeric society_ids; `onChange` receives
 * the new array. Closes on outside-click. Kept at module scope so it can be
 * shared between the top-of-page filter and the create-modal target picker.
 */
const SocietiesMultiSelect = ({
    societies,
    selected = [],
    onChange,
    placeholder = 'Select societies…',
    disabled = false,
}) => {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    const filtered = useMemo(() => {
        if (!q) return societies;
        const query = q.toLowerCase();
        return societies.filter(s => {
            const name = (s.name || s.society_name || '').toLowerCase();
            const code = String(s.society_code || '').toLowerCase();
            return name.includes(query) || code.includes(query);
        });
    }, [societies, q]);

    const filteredIds = useMemo(
        () => filtered.map(s => Number(s.society_id)),
        [filtered]
    );
    const allFilteredSelected = filteredIds.length > 0 && filteredIds.every(id => selected.includes(id));
    const someFilteredSelected = filteredIds.some(id => selected.includes(id));

    const toggleAll = () => {
        if (allFilteredSelected) {
            const drop = new Set(filteredIds);
            onChange(selected.filter(id => !drop.has(id)));
        } else {
            onChange(Array.from(new Set([...selected, ...filteredIds])));
        }
    };

    const toggleOne = (sid) => {
        const n = Number(sid);
        onChange(selected.includes(n) ? selected.filter(x => x !== n) : [...selected, n]);
    };

    const summary = () => {
        if (selected.length === 0) return placeholder;
        if (selected.length === societies.length) return 'All societies';
        if (selected.length === 1) {
            const s = societies.find(x => Number(x.society_id) === selected[0]);
            return s ? (s.name || s.society_name || `Society #${s.society_id}`) : '1 selected';
        }
        return `${selected.length} societies selected`;
    };

    return (
        <div ref={wrapRef} className="tw-relative">
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen(o => !o)}
                className="tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md tw-bg-white focus:tw-outline-none focus:tw-border-rose-500 hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
                <span className={`tw-truncate ${selected.length === 0 ? 'tw-text-gray-400' : 'tw-text-gray-800'}`}>
                    {summary()}
                </span>
                <Icon icon="mdi:chevron-down" className={`tw-text-gray-400 tw-transition-transform ${open ? 'tw-rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="tw-absolute tw-top-full tw-left-0 tw-right-0 tw-mt-1 tw-bg-white tw-border tw-border-gray-200 tw-rounded-md tw-shadow-lg tw-z-30 tw-flex tw-flex-col tw-max-h-80">
                    <div className="tw-p-2 tw-border-b tw-border-gray-100 tw-relative">
                        <Icon icon="mdi:magnify" className="tw-absolute tw-left-4 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                        <input
                            autoFocus
                            type="text"
                            value={q}
                            onChange={e => setQ(e.target.value)}
                            placeholder="Search…"
                            className="tw-w-full tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-xs tw-border tw-border-gray-100 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                        />
                    </div>
                    {filtered.length > 0 && (
                        <label className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-border-b tw-border-gray-100 tw-cursor-pointer tw-text-xs tw-font-semibold tw-text-gray-700 hover:tw-bg-gray-50">
                            <input
                                type="checkbox"
                                checked={allFilteredSelected}
                                ref={el => { if (el) el.indeterminate = !allFilteredSelected && someFilteredSelected; }}
                                onChange={toggleAll}
                                className="tw-accent-rose-600"
                            />
                            <span>Select all ({filtered.length})</span>
                        </label>
                    )}
                    <div className="tw-overflow-y-auto tw-flex-1">
                        {filtered.length === 0 && (
                            <p className="tw-p-3 tw-text-[11px] tw-text-gray-400 tw-text-center">No matches</p>
                        )}
                        {filtered.map(s => {
                            const checked = selected.includes(Number(s.society_id));
                            return (
                                <label
                                    key={s.society_id}
                                    className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-cursor-pointer tw-text-xs ${checked ? 'tw-bg-rose-50' : 'hover:tw-bg-gray-50'}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleOne(s.society_id)}
                                        className="tw-accent-rose-600"
                                    />
                                    <span className="tw-flex-1 tw-text-gray-800 tw-truncate">
                                        {s.name || s.society_name || `Society #${s.society_id}`}
                                    </span>
                                    {s.society_code && (
                                        <span className="tw-text-[10px] tw-text-gray-400 tw-font-mono">{s.society_code}</span>
                                    )}
                                </label>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const ComplaintCategoriesController = () => {
    const dispatch = useDispatch();

    const {
        categoryMutationLoading,
        categoriesMultiLoading,
        categoriesMultiData,
    } = useSelector(state => state.ComplaintReducer || {});
    const { usernamesDropdownData } = useSelector(state => state.EmployeeReducer || {});
    const assignableUsers = useMemo(
        () => (Array.isArray(usernamesDropdownData) ? usernamesDropdownData : []),
        [usernamesDropdownData]
    );

    // Webmaster picks the society context here — the same backend rules that
    // permit society admins for their own society apply to super-admin
    // callers here (they can operate on any society).
    // Single non-paginated endpoint (`/api/societies/dropdown`) — returns
    // every society the caller is entitled to see, no page limit. For
    // super-admins that's every active society; for scoped users it's the
    // societies they belong to.
    const { societyDropdownData, societyDropdownLoading: societiesLoading } = useSelector(
        state => state.CtrlEmployeeReducer || {}
    );
    const societies = useMemo(
        () => (Array.isArray(societyDropdownData) ? societyDropdownData : []),
        [societyDropdownData]
    );
    // The filter is a multi-select: an array of society_ids. Categories from
    // every ticked society are fetched in parallel and merged into `rows`.
    const [filterSocietyIds, setFilterSocietyIds] = useState([]);
    const [search, setSearch] = useState('');
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [deleteTarget, setDeleteTarget] = useState(null);
    // Create-mode-only: multi-select of target societies. Defaults to the
    // filter selection when the modal opens; the webmaster can tick extras
    // to bulk-apply the same category to N societies at once.
    const [targetSocieties, setTargetSocieties] = useState([]);
    const [bulkSaving, setBulkSaving] = useState(false);
    // Render mode: card grid or list table. In-memory only — no reason to
    // persist per-user; user picks on load and moves on.
    const [viewMode, setViewMode] = useState('card');

    // Rows come from redux (`categoriesMultiData`) — the dedicated
    // `/api/complaint-categories/multi` endpoint returns categories from every
    // filter-selected society in a single request, so no local merging.
    const rows = useMemo(
        () => (Array.isArray(categoriesMultiData) ? categoriesMultiData : []),
        [categoriesMultiData]
    );
    const categoriesLoading = categoriesMultiLoading;

    useEffect(() => {
        dispatch(getSocietyDropdown());
    }, [dispatch]);

    // Single request — the /multi endpoint takes a comma-separated society_ids
    // list and returns categories from all of them in one aggregated response
    // (server-side join with society + defaultAssignee). Empty filter means
    // "no society filter — return everything the caller can see," so we
    // dispatch regardless of the picker's current selection.
    const fetchList = useCallback(() => {
        dispatch(getComplaintCategoriesMulti({
            societyIds: filterSocietyIds,
            search: search || undefined,
        }));
    }, [dispatch, filterSocietyIds, search]);

    useEffect(() => { fetchList(); }, [fetchList]);

    // Refresh the assignable users pool. If exactly one society is in scope,
    // pull that society's employees. If multiple, we leave the pool empty
    // because the default-assignee dropdown is disabled anyway when the
    // create modal targets >1 society.
    useEffect(() => {
        if (filterSocietyIds.length === 1) {
            dispatch(getUsernamesDropdown({ societyId: filterSocietyIds[0] }));
        }
    }, [dispatch, filterSocietyIds]);

    const totalUsage = useMemo(
        () => rows.reduce((n, c) => n + Number(c.complaints_count || 0), 0),
        [rows]
    );
    // Lookup for rendering the "created in X society" chip on each card
    // when the filter spans multiple societies.
    const societyById = useMemo(() => {
        const m = new Map();
        societies.forEach(s => m.set(Number(s.society_id), s));
        return m;
    }, [societies]);

    const openCreate = () => {
        setForm(emptyForm);
        setErrors({});
        setModal({ mode: 'create' });
        // Seed the target picker with whichever societies are currently in
        // the filter — the webmaster can prune or add before submitting.
        setTargetSocieties(filterSocietyIds.length > 0
            ? [...filterSocietyIds]
            : societies.map(s => Number(s.society_id))
        );
    };
    const openEdit = (cat) => {
        setForm({
            name: cat.name || '',
            description: cat.description || '',
            icon: cat.icon || 'mdi:message-alert-outline',
            color: cat.color || 'slate',
            default_assignee_user_id: cat.default_assignee_user_id ? String(cat.default_assignee_user_id) : '',
        });
        setErrors({});
        setModal({ mode: 'edit', category: cat });
    };
    const closeModal = () => {
        setModal(null);
        setForm(emptyForm);
        setErrors({});
        setTargetSocieties([]);
    };

    const validate = () => {
        const e = {};
        if (modal?.mode === 'create' && targetSocieties.length === 0) {
            e.society = 'Pick at least one target society';
        }
        if (!form.name.trim()) e.name = 'Name is required';
        else if (form.name.trim().length < 2) e.name = 'Minimum 2 characters';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;

        // ---- Edit: single society, use the existing single-record endpoint ----
        if (modal.mode === 'edit') {
            const payload = {
                name: form.name.trim(),
                description: form.description.trim() || undefined,
                icon: form.icon || undefined,
                color: form.color || undefined,
                default_assignee_user_id: form.default_assignee_user_id
                    ? Number(form.default_assignee_user_id)
                    : null,
            };
            dispatch(updateComplaintCategory({
                id: modal.category.category_id,
                ...payload,
                onSuccess: () => { closeModal(); fetchList(); },
            }));
            return;
        }

        // ---- Create: single API call, backend fans it out across every
        // target society and returns a per-society breakdown. Assignee is
        // omitted because it's a per-society FK — set it later by editing
        // each society's copy individually. ----
        setBulkSaving(true);
        dispatch(bulkCreateComplaintCategories({
            society_ids: targetSocieties,
            name: form.name.trim(),
            description: form.description.trim() || undefined,
            icon: form.icon || undefined,
            color: form.color || undefined,
            onSuccess: (res) => {
                setBulkSaving(false);
                const s = res?.data || {};
                const { createdCount = 0, skippedCount = 0, failedCount = 0, total = 0 } = s;
                if (createdCount === total && total > 0) {
                    notify('success', `Category created in ${createdCount} societ${createdCount === 1 ? 'y' : 'ies'}`);
                } else if (createdCount > 0) {
                    const parts = [`Created in ${createdCount}`];
                    if (skippedCount) parts.push(`skipped ${skippedCount} (duplicate name)`);
                    if (failedCount) parts.push(`failed ${failedCount}`);
                    notify('warning', parts.join(', '));
                } else {
                    notify('error', 'Could not create the category — every target society either already has one or errored.');
                }
                closeModal();
                fetchList();
            },
            onError: () => {
                setBulkSaving(false);
            },
        }));
    };

    const handleDelete = () => {
        if (!deleteTarget) return;
        dispatch(deleteComplaintCategory({
            id: deleteTarget.category_id,
            onSuccess: () => { setDeleteTarget(null); fetchList(); },
        }));
    };

    const activeCat = modal?.mode === 'edit' ? modal.category : null;
    const currentColor = colorFor(form.color);
    // Show the per-card society tag whenever the view covers more than one
    // society — either because multiple ids are ticked or because the filter
    // is empty (which now means "every society").
    const showSocietyBadge = filterSocietyIds.length !== 1;

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-max-w-6xl tw-mx-auto tw-space-y-4">
                {/* Header + society filter */}
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                    <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
                        <div>
                            <h1 className="tw-text-base tw-font-bold tw-text-gray-800">Complaint Categories</h1>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Manage categories for any society you administer</p>
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-2">
                            {/* Card / List view toggle — segmented control */}
                            <div className="tw-inline-flex tw-p-0.5 tw-bg-gray-100 tw-rounded-md">
                                <button
                                    type="button"
                                    onClick={() => setViewMode('card')}
                                    title="Card view"
                                    className={`tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium tw-transition-colors ${viewMode === 'card' ? 'tw-bg-white tw-text-gray-800 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}
                                >
                                    <Icon icon="mdi:view-grid-outline" className="tw-text-base" />
                                    Cards
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setViewMode('list')}
                                    title="List view"
                                    className={`tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium tw-transition-colors ${viewMode === 'list' ? 'tw-bg-white tw-text-gray-800 tw-shadow-sm' : 'tw-text-gray-500 hover:tw-text-gray-700'}`}
                                >
                                    <Icon icon="mdi:format-list-bulleted" className="tw-text-base" />
                                    List
                                </button>
                            </div>
                            <button
                                onClick={openCreate}
                                disabled={societies.length === 0}
                                className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-shadow-sm disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                            >
                                <Icon icon="mdi:plus" className="tw-text-base" />
                                New Category
                            </button>
                        </div>
                    </div>

                    <div className="tw-mt-4 tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                        <div className="sm:tw-col-span-2">
                            <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">Filter by Society</label>
                            <SocietiesMultiSelect
                                societies={societies}
                                selected={filterSocietyIds}
                                onChange={setFilterSocietyIds}
                                placeholder={societiesLoading ? 'Loading…' : 'All societies (no filter)'}
                                disabled={societiesLoading}
                            />
                        </div>
                        <div className="tw-relative">
                            <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">Filter Categories</label>
                            <Icon icon="mdi:magnify" className="tw-absolute tw-left-3 tw-top-9 tw-text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="tw-w-full tw-pl-9 tw-pr-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500 tw-bg-white"
                            />
                        </div>
                    </div>

                    <div className="tw-mt-4 tw-flex tw-flex-wrap tw-gap-2">
                        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold tw-rounded-full tw-bg-gray-100 tw-text-gray-700">
                            <Icon icon="mdi:tag-multiple-outline" /> {rows.length} categor{rows.length === 1 ? 'y' : 'ies'}
                        </span>
                        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold tw-rounded-full tw-bg-blue-50 tw-text-blue-700">
                            <Icon icon="mdi:message-badge-outline" /> {totalUsage} complaints filed
                        </span>
                        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold tw-rounded-full tw-bg-slate-50 tw-text-slate-600">
                            <Icon icon="mdi:city-variant-outline" />
                            {filterSocietyIds.length === 0
                                ? `across all ${societies.length} societ${societies.length === 1 ? 'y' : 'ies'}`
                                : `across ${filterSocietyIds.length} societ${filterSocietyIds.length === 1 ? 'y' : 'ies'}`}
                        </span>
                    </div>
                </div>

                {/* Grid */}
                {categoriesLoading ? (
                    <div className="tw-flex tw-justify-center tw-py-16">
                        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-2 tw-border-gray-200 tw-border-t-rose-500" />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-dashed tw-border-gray-200 tw-p-10 tw-text-center">
                        <div className="tw-mx-auto tw-w-12 tw-h-12 tw-rounded-full tw-bg-rose-50 tw-text-rose-500 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="mdi:tag-plus-outline" className="tw-text-2xl" />
                        </div>
                        <h3 className="tw-mt-3 tw-text-sm tw-font-bold tw-text-gray-800">No categories found</h3>
                        <p className="tw-mt-1 tw-text-[11px] tw-text-gray-500">
                            {search
                                ? 'No categories match that search. Try clearing the text filter.'
                                : filterSocietyIds.length > 0
                                    ? 'No categories in the selected societies yet.'
                                    : 'No categories exist across any of your societies yet.'}
                        </p>
                        <button
                            onClick={openCreate}
                            className="tw-mt-4 tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700"
                        >
                            <Icon icon="mdi:plus" /> Create Category
                        </button>
                    </div>
                ) : viewMode === 'card' ? (
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-3">
                        {rows.map(cat => {
                            const color = colorFor(cat.color);
                            const count = Number(cat.complaints_count || 0);
                            return (
                                <div
                                    key={cat.category_id}
                                    className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm hover:tw-shadow-md tw-transition-all tw-p-4"
                                >
                                    <div className="tw-flex tw-items-start tw-gap-3">
                                        <div className={`tw-w-11 tw-h-11 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${color.bg} ${color.text}`}>
                                            <Icon icon={iconFor(cat.icon)} className="tw-text-xl" />
                                        </div>
                                        <div className="tw-min-w-0 tw-flex-1">
                                            <h3 className="tw-text-sm tw-font-bold tw-text-gray-800 tw-truncate">{cat.name}</h3>
                                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5 tw-line-clamp-2">
                                                {cat.description || 'No description'}
                                            </p>
                                            {showSocietyBadge && (
                                                <p className="tw-mt-2 tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-font-semibold tw-text-slate-600 tw-bg-slate-50 tw-px-1.5 tw-py-0.5 tw-rounded tw-mr-1">
                                                    <Icon icon="mdi:city-variant-outline" />
                                                    {cat.society?.name || societyById.get(Number(cat.society_id))?.name || `Society #${cat.society_id}`}
                                                </p>
                                            )}
                                            {cat.defaultAssignee && (
                                                <p className="tw-mt-2 tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-600 tw-bg-gray-50 tw-px-1.5 tw-py-0.5 tw-rounded">
                                                    <Icon icon="mdi:account-arrow-right-outline" /> Assigns to {fullName(cat.defaultAssignee)}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="tw-mt-3 tw-flex tw-items-center tw-justify-between tw-pt-3 tw-border-t tw-border-gray-100">
                                        <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${color.bg} ${color.text}`}>
                                            <Icon icon="mdi:message-badge-outline" /> {count} complaint{count === 1 ? '' : 's'}
                                        </span>
                                        <div className="tw-inline-flex tw-items-center tw-gap-1">
                                            <button
                                                title="Edit"
                                                onClick={() => openEdit(cat)}
                                                className="tw-p-1.5 tw-rounded hover:tw-bg-amber-50 tw-text-amber-600"
                                            >
                                                <Icon icon="mdi:pencil-outline" className="tw-text-base" />
                                            </button>
                                            <button
                                                title={count > 0 ? `In use by ${count} complaint(s)` : 'Delete'}
                                                onClick={() => setDeleteTarget(cat)}
                                                className="tw-p-1.5 tw-rounded hover:tw-bg-rose-50 tw-text-rose-600"
                                            >
                                                <Icon icon="mdi:trash-can-outline" className="tw-text-base" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    /* List / table view — dense, scannable. Society column hides
                       when only one society is in scope (single-society view). */
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                        <div className="tw-overflow-x-auto">
                            <table className="tw-w-full tw-text-xs">
                                <thead>
                                    <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100 tw-text-gray-500">
                                        <th className="tw-text-left tw-px-4 tw-py-2.5 tw-font-semibold">Category</th>
                                        {showSocietyBadge && (
                                            <th className="tw-text-left tw-px-4 tw-py-2.5 tw-font-semibold">Society</th>
                                        )}
                                        <th className="tw-text-left tw-px-4 tw-py-2.5 tw-font-semibold">Default Assignee</th>
                                        <th className="tw-text-right tw-px-4 tw-py-2.5 tw-font-semibold">Complaints</th>
                                        <th className="tw-text-right tw-px-4 tw-py-2.5 tw-font-semibold">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(cat => {
                                        const color = colorFor(cat.color);
                                        const count = Number(cat.complaints_count || 0);
                                        const societyName = cat.society?.name
                                            || societyById.get(Number(cat.society_id))?.name
                                            || `Society #${cat.society_id}`;
                                        return (
                                            <tr key={cat.category_id} className="tw-border-b tw-border-gray-50 hover:tw-bg-gray-50/40">
                                                <td className="tw-px-4 tw-py-2.5">
                                                    <div className="tw-flex tw-items-center tw-gap-2">
                                                        <div className={`tw-w-8 tw-h-8 tw-rounded-md tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${color.bg} ${color.text}`}>
                                                            <Icon icon={iconFor(cat.icon)} className="tw-text-sm" />
                                                        </div>
                                                        <div className="tw-min-w-0">
                                                            <p className="tw-font-semibold tw-text-gray-800 tw-truncate">{cat.name}</p>
                                                            {cat.description && (
                                                                <p className="tw-text-[10px] tw-text-gray-500 tw-truncate">{cat.description}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                {showSocietyBadge && (
                                                    <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">
                                                        <span className="tw-inline-flex tw-items-center tw-gap-1">
                                                            <Icon icon="mdi:city-variant-outline" className="tw-text-slate-400" />
                                                            {societyName}
                                                        </span>
                                                    </td>
                                                )}
                                                <td className="tw-px-4 tw-py-2.5 tw-text-gray-700">
                                                    {cat.defaultAssignee ? fullName(cat.defaultAssignee) : <span className="tw-text-gray-400">Unassigned</span>}
                                                </td>
                                                <td className="tw-px-4 tw-py-2.5 tw-text-right">
                                                    <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-rounded-full ${color.bg} ${color.text}`}>
                                                        {count}
                                                    </span>
                                                </td>
                                                <td className="tw-px-4 tw-py-2.5 tw-text-right">
                                                    <div className="tw-inline-flex tw-items-center tw-gap-1">
                                                        <button
                                                            title="Edit"
                                                            onClick={() => openEdit(cat)}
                                                            className="tw-p-1.5 tw-rounded hover:tw-bg-amber-50 tw-text-amber-600"
                                                        >
                                                            <Icon icon="mdi:pencil-outline" className="tw-text-base" />
                                                        </button>
                                                        <button
                                                            title={count > 0 ? `In use by ${count} complaint(s)` : 'Delete'}
                                                            onClick={() => setDeleteTarget(cat)}
                                                            className="tw-p-1.5 tw-rounded hover:tw-bg-rose-50 tw-text-rose-600"
                                                        >
                                                            <Icon icon="mdi:trash-can-outline" className="tw-text-base" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Create / Edit modal */}
            {modal && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-lg tw-max-h-[90vh] tw-flex tw-flex-col tw-overflow-hidden">
                        <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between">
                            <div className="tw-flex tw-items-center tw-gap-3">
                                <div className={`tw-w-9 tw-h-9 tw-rounded-lg tw-flex tw-items-center tw-justify-center ${currentColor.bg} ${currentColor.text}`}>
                                    <Icon icon={iconFor(form.icon)} className="tw-text-lg" />
                                </div>
                                <div>
                                    <h3 className="tw-text-sm tw-font-bold tw-text-gray-800">
                                        {modal.mode === 'create' ? 'New Category' : 'Edit Category'}
                                    </h3>
                                    <p className="tw-text-[11px] tw-text-gray-500">
                                        {modal.mode === 'edit'
                                            ? (societyById.get(Number(activeCat?.society_id))?.name || `Society #${activeCat?.society_id}`)
                                            : (targetSocieties.length === 0
                                                ? 'Pick target societies below'
                                                : targetSocieties.length === 1
                                                    ? (societyById.get(targetSocieties[0])?.name || `Society #${targetSocieties[0]}`)
                                                    : `${targetSocieties.length} societies selected`)}
                                        {activeCat?.complaints_count > 0 ? ` · used by ${activeCat.complaints_count} complaint(s)` : ''}
                                    </p>
                                </div>
                            </div>
                            <button onClick={closeModal} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500">
                                <Icon icon="mdi:close" className="tw-text-lg" />
                            </button>
                        </div>

                        <div className="tw-p-5 tw-space-y-4 tw-overflow-y-auto">
                            {errors.society && (
                                <p className="tw-text-[10px] tw-text-rose-600">{errors.society}</p>
                            )}

                            <div>
                                <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">
                                    Name <span className="tw-text-rose-500">*</span>
                                </label>
                                <input
                                    value={form.name}
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                    className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                                    placeholder="e.g. Plumbing"
                                />
                                {errors.name && <p className="tw-text-[10px] tw-text-rose-600 tw-mt-1">{errors.name}</p>}
                            </div>

                            <div>
                                <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">Description</label>
                                <textarea
                                    rows={2}
                                    value={form.description}
                                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                    className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                                    placeholder="Optional — what this category covers"
                                />
                            </div>

                            {/* Target societies — only for create; edit is scoped to the
                                one society the category already belongs to. */}
                            {modal.mode === 'create' && (
                                <div>
                                    <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">
                                        Target Societies <span className="tw-text-rose-500">*</span>
                                    </label>
                                    <p className="tw-text-[10px] tw-text-gray-500 tw-mb-2">
                                        Same name, icon and color are applied to every ticked society.
                                    </p>
                                    <SocietiesMultiSelect
                                        societies={societies}
                                        selected={targetSocieties}
                                        onChange={setTargetSocieties}
                                        placeholder="Pick target societies…"
                                    />
                                    {errors.society && (
                                        <p className="tw-text-[10px] tw-text-rose-600 tw-mt-1">{errors.society}</p>
                                    )}
                                </div>
                            )}

                            {/* Default assignee — pool is the picked society's members.
                                Only meaningful when targeting a single society, since
                                the assignee FK is society-scoped. */}
                            <div>
                                <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-1">Default Assignee</label>
                                <select
                                    value={form.default_assignee_user_id}
                                    onChange={e => setForm(f => ({ ...f, default_assignee_user_id: e.target.value }))}
                                    disabled={modal.mode === 'create' && targetSocieties.length > 1}
                                    className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500 disabled:tw-bg-gray-50 disabled:tw-text-gray-400 disabled:tw-cursor-not-allowed"
                                >
                                    <option value="">Unassigned (route manually)</option>
                                    {assignableUsers.map(u => (
                                        <option key={u.user_id} value={u.user_id}>{fullName(u)}</option>
                                    ))}
                                </select>
                                <p className="tw-text-[10px] tw-text-gray-500 tw-mt-1">
                                    {modal.mode === 'create' && targetSocieties.length > 1
                                        ? 'Not available when creating for multiple societies — the assignee is society-specific. Edit each society\'s copy afterwards to set one.'
                                        : 'Every complaint raised under this category in this society auto-assigns to this person.'}
                                </p>
                            </div>

                            <div>
                                <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-2">Color</label>
                                <div className="tw-flex tw-flex-wrap tw-gap-2">
                                    {COLOR_PALETTE.map(c => (
                                        <button
                                            key={c.key}
                                            type="button"
                                            onClick={() => setForm(f => ({ ...f, color: c.key }))}
                                            title={c.key}
                                            className={`tw-w-8 tw-h-8 tw-rounded-full ${c.swatch} tw-transition-all ${form.color === c.key ? `tw-ring-2 tw-ring-offset-2 ${c.ring}` : 'hover:tw-scale-110'}`}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-600 tw-mb-2">Icon</label>
                                <div className="tw-grid tw-grid-cols-8 tw-gap-2">
                                    {ICON_LIBRARY.map(i => (
                                        <button
                                            key={i.key}
                                            type="button"
                                            onClick={() => setForm(f => ({ ...f, icon: i.key }))}
                                            title={i.label}
                                            className={`tw-aspect-square tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-transition-all ${form.icon === i.key ? `${currentColor.bg} ${currentColor.text} tw-border-transparent tw-ring-2 ${currentColor.ring}` : 'tw-border-gray-200 tw-text-gray-500 hover:tw-bg-gray-50'}`}
                                        >
                                            <Icon icon={i.key} className="tw-text-lg" />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="tw-pt-2 tw-border-t tw-border-gray-100">
                                <p className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-400 tw-font-semibold tw-mb-2">Preview</p>
                                <div className="tw-inline-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-border-gray-100">
                                    <div className={`tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center ${currentColor.bg} ${currentColor.text}`}>
                                        <Icon icon={iconFor(form.icon)} />
                                    </div>
                                    <div>
                                        <p className="tw-text-xs tw-font-semibold tw-text-gray-800">{form.name || 'Category name'}</p>
                                        <p className="tw-text-[10px] tw-text-gray-500">{form.description || 'No description'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="tw-p-4 tw-border-t tw-border-gray-100 tw-flex tw-justify-end tw-gap-2 tw-bg-gray-50/50">
                            <button onClick={closeModal} className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50 tw-bg-white">Cancel</button>
                            <button
                                onClick={handleSave}
                                disabled={categoryMutationLoading || bulkSaving}
                                className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50"
                            >
                                {(categoryMutationLoading || bulkSaving) && <div className="tw-animate-spin tw-rounded-full tw-h-3 tw-w-3 tw-border-2 tw-border-white tw-border-t-transparent" />}
                                {modal.mode === 'edit'
                                    ? 'Save'
                                    : targetSocieties.length > 1
                                        ? `Create in ${targetSocieties.length} societies`
                                        : 'Create'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete confirm */}
            {deleteTarget && (
                <div className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
                    <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-sm tw-overflow-hidden">
                        <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-3">
                            <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-rose-50 tw-text-rose-600 tw-flex tw-items-center tw-justify-center">
                                <Icon icon="mdi:alert-outline" className="tw-text-lg" />
                            </div>
                            <div>
                                <h3 className="tw-text-sm tw-font-bold tw-text-gray-800">Delete Category?</h3>
                                <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Existing complaints keep the category; it just won't appear in new complaint forms.</p>
                            </div>
                        </div>
                        <div className="tw-p-5">
                            {Number(deleteTarget.complaints_count) > 0 && (
                                <div className="tw-mb-3 tw-p-2 tw-bg-amber-50 tw-border tw-border-amber-100 tw-rounded-md">
                                    <p className="tw-text-[11px] tw-text-amber-800">
                                        <Icon icon="mdi:information-outline" className="tw-inline tw-mr-1" />
                                        This category is used by <strong>{deleteTarget.complaints_count}</strong> complaint(s).
                                    </p>
                                </div>
                            )}
                            <p className="tw-text-xs tw-text-gray-600 tw-mb-4">
                                Delete <span className="tw-font-semibold">{deleteTarget.name}</span>?
                            </p>
                            <div className="tw-flex tw-justify-end tw-gap-2">
                                <button onClick={() => setDeleteTarget(null)} className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50">Cancel</button>
                                <button
                                    onClick={handleDelete}
                                    disabled={categoryMutationLoading}
                                    className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-rounded-md disabled:tw-opacity-50"
                                >
                                    {categoryMutationLoading ? 'Deleting…' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ComplaintCategoriesController;
